import React from 'react';
import { useLocation } from 'react-router-dom';
import { ClipboardList, History, Wallet, MoreHorizontal } from 'lucide-react';
import { BottomNav } from '@parsina/framework';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';
import { useAppNavigate } from '../../hooks/useAppNavigate';
import { useChatUnread } from '../../lib/chatRuntime';
import { formatUnread } from '../../screens/Messages/Messages.shared';
import { useDoctor } from '@parsina/framework/clinical/src/hooks/useDoctor';
import { useWalletBalance } from '@parsina/framework/clinical/src/hooks/useWalletBalance';
import { useReceptionStrings } from '../../lib/receptionStrings';
import MobileMoreSheet from './MobileMoreSheet';

// The mobile shell's bottom navigation (mobile concept §3.1) — a thin wrapper
// over the kit's `BottomNav`, which owns the bar itself: real kit buttons at
// ≥2.75rem, `aria-current="page"`, the safe-area inset, the navy count badge.
//
// What stays here is the part the kit must NOT know: routing, the visit
// leave-guard, and the unread count. `formatUnread` is the same helper the
// Messages list and the desktop sidebar use, so one cap rule («۹+») and one
// digit locale across all three.
//
// FOUR slots, and four is the ceiling — the clinic panel's pattern: the three
// destinations of the working day, then «بیشتر». حسابداری is a tab because the
// day's income lives there now (it left the reception); پیام‌ها, پروفایل and
// اعتبار fold into the more-sheet, which also names the doctor and their پارسه
// balance (the reception header no longer greets). The unread count rides the
// «بیشتر» tab, because that is where پیام‌ها is.

export const MORE_KEY = 'more';

const NAV_ITEMS = [
  // «خانه» named the tab by its position in the shell, not by what it does. The
  // destination is the پذیرش desk, so the tab says so — same word as the sidebar.
  { key: 'reception',  labelKey: 'dashboard.title',       Icon: ClipboardList },
  { key: 'encounters', labelKey: 'nav.mobile.encounters', Icon: History },
  { key: 'accounting', labelKey: 'nav.items.accounting',  Icon: Wallet },
];

/** Destinations behind «بیشتر»: the tab lights up while one of them is open. */
export const MORE_DESTINATIONS = ['messages', 'profile', 'credits', 'my-page'];

const MobileBottomNav = () => {
  const { t, formatNumber } = useTranslation();
  const rs = useReceptionStrings();
  const location = useLocation();
  const navigate = useAppNavigate();
  const { doctor } = useDoctor();
  const balance = useWalletBalance(doctor?.id);
  const [moreOpen, setMoreOpen] = React.useState(false);

  const unread = formatUnread(useChatUnread(), formatNumber);

  const items = [
    ...NAV_ITEMS.map(({ key, labelKey, Icon }) => ({
      key, label: t(labelKey), icon: <Icon size={22} aria-hidden />,
    })),
    {
      key: MORE_KEY,
      label: rs('nav.mobile.more'),
      icon: <MoreHorizontal size={22} aria-hidden />,
      // `formatUnread` returns '' at zero, and an empty string is not a badge —
      // the kit renders anything that is neither null nor 0.
      badge: unread || undefined,
      badgeLabel: unread ? t('messages.unreadBadge', { count: unread }) : undefined,
      // An unread tally is waiting, not wrong — navy, never the destructive red.
      badgeTone: 'count',
    },
  ];

  // An open visit intercepts navigation with its own confirm.
  const go = (key) => {
    if (window.__parsina_visit_guard) window.__parsina_visit_guard(() => navigate(key));
    else navigate(key);
  };

  const here = location.pathname.slice(1).split('/')[0];
  const active = MORE_DESTINATIONS.includes(here) ? MORE_KEY : here;

  return (
    <>
      <BottomNav
        data-testid="mobile-bottom-nav"
        items={items}
        active={active}
        onSelect={(key) => (key === MORE_KEY ? setMoreOpen(true) : go(key))}
      />
      <MobileMoreSheet
        open={moreOpen}
        onClose={() => setMoreOpen(false)}
        onNavigate={go}
        doctor={doctor}
        balance={balance}
        unread={unread}
      />
    </>
  );
};

export default MobileBottomNav;
